/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { Alert, Button } from 'reactstrap';
import { RunStatusBadge } from './partials/RunStatusBadge';
import { InsightProgressBar } from './partials/InsightProgressBar';
import { GenerationRunStatus, type GenerationRun } from '@g17eco/types/insights-synthesis';
import { formatDate, DATE } from '@utils/date';

interface RunHeaderProps {
  run: GenerationRun;
  isProcessing: boolean;
  onRetry?: () => void;
  isRetrying?: boolean;
}

const formatDateTime = (dateString: string) => formatDate(dateString, DATE.AT_TIME);

// Cooldown period (1 hour) - matches backend
const ONE_HOUR_MS = 60 * 60 * 1000;

/**
 * Check if retry is on cooldown.
 * Uses lastRetryAt if available, otherwise falls back to createdAt.
 */
const isOnCooldown = (run: GenerationRun): boolean => {
  const referenceTime = run.lastRetryAt ?? run.createdAt;
  const referenceTimestamp = new Date(referenceTime).getTime();
  return Date.now() - referenceTimestamp < ONE_HOUR_MS;
};

/**
 * Check if retry is allowed for this run.
 */
const canRetry = (run: GenerationRun): boolean => {
  // Cannot retry completed runs (terminal state)
  if (run.status === GenerationRunStatus.Completed) {
    return false;
  }
  // Check cooldown
  return !isOnCooldown(run);
};

export const RunHeader = ({ run, isProcessing, onRetry, isRetrying }: RunHeaderProps) => {
  return (
    <div className='run-header mb-4'>
      <div className='d-flex justify-content-between align-items-start mb-3'>
        <div>
          <h5 className='mb-1'>{run.config.sectionType ?? 'All Sections'}</h5>
          <div className='text-muted small'>
            Created: {formatDateTime(run.createdAt)}
            {run.completedAt && ` • Completed: ${formatDateTime(run.completedAt)}`}
          </div>
          {isProcessing && (
            <span className='text-muted small'>
              <i className='fa fa-sync fa-spin me-1' />
              Auto-refreshing
            </span>
          )}
        </div>
        <div className='d-flex align-items-center gap-2'>
          <RunStatusBadge status={run.status} />
          {onRetry && (
            <Button
              color='primary'
              size='sm'
              onClick={onRetry}
              disabled={isRetrying || !canRetry(run)}
            >
              {isRetrying ? (
                <>
                  <i className='fa fa-spinner fa-spin me-1' />
                  Retrying...
                </>
              ) : (
                <>
                  <i className='fa fa-play me-1' />
                  Retry
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {run.error && (
        <Alert color='danger' className='mb-3'>
          <strong>Error:</strong> {run.error.message}
        </Alert>
      )}

      <div>
        <small className='text-muted d-block mb-2'>Progress</small>
        <InsightProgressBar progress={run.progress} />
      </div>
    </div>
  );
};
